// Salaisten kohteiden hallinta (vain ylläpitäjälle): lista ja lisäys-/muokkauslomake.
//
// Pelaaja ei näe tätä lainkaan. Palvelin tarkistaa ylläpitoavaimen joka pyynnöllä, joten näkymän
// piilotus on vain siisteyttä; oikea lukko on palvelimella (backend/app/admin.py).

import { useEffect, useState } from "preact/hooks";

import { adminLine, emptyForm, formFromPlace, formToPayload, LIMITS } from "../lib/hidden.js";
import { Sheet } from "./Sheet.jsx";

/** Ylläpitäjän lista: kaikki salaiset kohteet paikkoineen sekä uuden lisäys. */
export function HiddenAdminPanel({ app }) {
  const [places, setPlaces] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    app.api.adminHidden().then(
      (r) => {
        if (!alive) return;
        setPlaces(r.places);
        setError(null);
      },
      (e) => alive && setError(e.status === 401 ? "Ylläpitoavain ei kelpaa enää. Ota ylläpito uudelleen käyttöön Passin asetuksista." : e.message),
    );
    return () => {
      alive = false;
    };
  }, [app.dataVersion, app.admin]);

  return (
    <>
      <p class="s">
        Salaiset kohteet eivät näy pelaajille mistään ennen kuin he ovat vihjesäteellä. Löytö vaatii käynnin lihasvoimalla käyntisäteen sisällä.
      </p>
      <div class="sheet-actions">
        <button class="btn btn-rasti" onClick={() => app.openHiddenForm(emptyForm(app.me))}>Lisää salainen kohde</button>
      </div>
      {!app.me && <p class="s">Sijaintiasi ei tiedetä. Voit silti valita paikan kartalta lomakkeessa.</p>}
      {error && <p class="rec-note warn" role="alert">{error}</p>}
      {places?.length === 0 && <div class="empty"><b>Ei vielä salaisia kohteita</b>Lisää ensimmäinen seisomalla paikalla ja valitsemalla Lisää salainen kohde.</div>}
      {places?.map((p) => (
        <button class="item hidden-admin" key={p.id} onClick={() => app.openHiddenForm(formFromPlace(p))}>
          <svg class="mark" viewBox="0 0 30 30" aria-hidden="true">
            <path d="M15 4l3.4 7.2 7.8 1-5.7 5.4 1.5 7.7L15 21.4 8 25.3l1.5-7.7-5.7-5.4 7.8-1z" fill="none" stroke={p.published ? "var(--gold)" : "var(--ink-soft)"} stroke-width="2.2" stroke-linejoin="round" />
          </svg>
          <div>
            <div class="t">{p.name}</div>
            <div class="s">{adminLine(p)}</div>
          </div>
          <div class="end">Muokkaa</div>
        </button>
      ))}
    </>
  );
}

/** Lisäys- ja muokkauslomake. */
export function HiddenFormSheet({ app, form: initial, onClose }) {
  const [f, setF] = useState(initial);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));
  const text = (key) => ({ value: f[key], onInput: (e) => set({ [key]: e.currentTarget.value }) });

  // Sama lomake voi avautua uudelleen toisella sisällöllä (paluu kartan valinnasta).
  useEffect(() => setF(initial), [initial]);

  const useHere = () => {
    if (!navigator.geolocation) return setErrors(["Paikannus ei ole käytettävissä. Valitse paikka kartalta."]);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        set({ lat: p.coords.latitude.toFixed(6), lon: p.coords.longitude.toFixed(6) });
        setErrors([]);
        setLocating(false);
      },
      () => {
        setErrors(["Sijaintia ei saatu. Salli sijainti tai valitse paikka kartalta."]);
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 20_000, maximumAge: 5000 },
    );
  };

  const pickOnMap = () => {
    const lat = Number(f.lat.replace(",", "."));
    const lon = Number(f.lon.replace(",", "."));
    const start = f.lat && f.lon && Number.isFinite(lat) && Number.isFinite(lon) ? [lat, lon] : app.me ? [app.me.latitude, app.me.longitude] : null;
    app.openHiddenForm(null);
    app.startTool({ type: "hiddenpick", draft: f, radius: Number(f.visitRadius) || 30, start });
  };

  const save = async () => {
    const { payload, errors: found } = formToPayload(f);
    setErrors(found);
    if (found.length) return;
    setBusy(true);
    try {
      await app.api.saveHidden(f.id, payload);
      app.toast({ kind: "info", what: f.id ? "Salainen kohde tallennettu" : "Salainen kohde lisätty", sub: payload.published ? null : "Se on luonnos, eikä pelaajat näe sitä." });
      app.refresh();
      onClose();
    } catch (e) {
      setErrors([e.message]);
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await app.api.deleteHidden(f.id);
      app.toast({ kind: "info", what: "Salainen kohde poistettu", sub: "Sen löydöt perutaan pistehistoriaan oikaisuna." });
      app.refresh();
      onClose();
    } catch (e) {
      setErrors([e.message]);
      setBusy(false);
    }
  };


  return (
    <Sheet title={f.id ? "Muokkaa salaista kohdetta" : "Uusi salainen kohde"} onClose={onClose}>
      <div class="field">
        <label for="hd-name">Nimi</label>
        <input id="hd-name" maxLength={LIMITS.name} autocomplete="off" placeholder="Esimerkiksi: Kallion kupeen lähde" {...text("name")} />
      </div>
      <div class="field">
        <label for="hd-hint">Vihje, näkyy kun pelaaja on lähellä</label>
        <input id="hd-hint" maxLength={LIMITS.hint} autocomplete="off" placeholder="Esimerkiksi: Vesi kuuluu ennen kuin sen näkee." {...text("hint")} />
      </div>
      <div class="field">
        <label for="hd-desc">Kuvaus, näkyy vasta löydön jälkeen</label>
        <textarea id="hd-desc" maxLength={LIMITS.description} {...text("description")} />
      </div>

      <div class="field">
        <span class="label">Paikka</span>
        <div class="coord-row">
          <input aria-label="Leveysaste" inputMode="decimal" placeholder="Leveysaste" {...text("lat")} />
          <input aria-label="Pituusaste" inputMode="decimal" placeholder="Pituusaste" {...text("lon")} />
        </div>
        <div class="tool-actions">
          <button class="btn" disabled={locating} onClick={useHere}>{locating ? "Haetaan…" : "Käytä nykyistä sijaintia"}</button>
          <button class="btn" onClick={pickOnMap}>Valitse kartalta</button>
        </div>
      </div>

      <div class="field-row">
        <div class="field">
          <label for="hd-hr">Vihjesäde (m)</label>
          <input id="hd-hr" inputMode="numeric" {...text("hintRadius")} />
        </div>
        <div class="field">
          <label for="hd-vr">Käyntisäde (m)</label>
          <input id="hd-vr" inputMode="numeric" {...text("visitRadius")} />
        </div>
        <div class="field">
          <label for="hd-pts">Pisteet</label>
          <input id="hd-pts" inputMode="numeric" {...text("points")} />
        </div>
      </div>
      <p class="s">Vihje näkyy {f.hintRadius || "?"} m:n päässä ja löytö tunnistetaan {f.visitRadius || "?"} m:n sisällä. Auton tai muun moottorikulun matkalla kohde ei löydy.</p>

      <div class="field">
        <span class="label">Milloin löydettävissä</span>
        <label class="check"><input type="checkbox" checked={!f.limited} onChange={(e) => set({ limited: !e.currentTarget.checked })} /><span>Aina</span></label>
        {f.limited && (
          <div class="field-row">
            <div class="field"><label for="hd-vf">Alkupäivä</label><input id="hd-vf" type="date" {...text("validFrom")} /></div>
            <div class="field"><label for="hd-vt">Loppupäivä</label><input id="hd-vt" type="date" {...text("validTo")} /></div>
          </div>
        )}
        <label class="check"><input type="checkbox" checked={f.daily} onChange={(e) => set({ daily: e.currentTarget.checked })} /><span>Vain tiettyyn kellonaikaan</span></label>
        {f.daily && (
          <div class="field-row">
            <div class="field"><label for="hd-df">Alkaen</label><input id="hd-df" type="time" {...text("dailyFrom")} /></div>
            <div class="field"><label for="hd-dt">Päättyen</label><input id="hd-dt" type="time" {...text("dailyTo")} /></div>
          </div>
        )}
      </div>

      <label class="check"><input type="checkbox" checked={f.published} onChange={(e) => set({ published: e.currentTarget.checked })} /><span>Julkaistu (pois päältä: luonnos, jota kukaan ei näe)</span></label>
      {f.id && <p class="s">Jos siirrät kohteen tai muutat käyntisädettä, aiemmat löydöt perutaan ja kohde pitää löytää uudelleen.</p>}

      {errors.length > 0 && (
        <div class="notice warn" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}

      <div class="sheet-actions">
        <button class="btn btn-rasti btn-block" disabled={busy} onClick={save}>{f.id ? "Tallenna muutokset" : "Lisää kohde"}</button>
        <button class="btn btn-block" onClick={onClose}>Peruuta</button>
        {f.id && !confirmDelete && <button class="btn btn-quiet danger-text" onClick={() => setConfirmDelete(true)}>Poista kohde</button>}
        {f.id && confirmDelete && (
          <>
            <p class="s">Poistaminen perii kaikkien pelaajien löydöt tästä kohteesta.</p>
            <button class="btn btn-danger btn-block" disabled={busy} onClick={remove}>Poista lopullisesti</button>
            <button class="btn btn-quiet" onClick={() => setConfirmDelete(false)}>Säilytä</button>
          </>
        )}
      </div>
    </Sheet>
  );
}
